import React from 'react';

const cx = (...a) => a.filter(Boolean).join(' ');

const sizeMap = {
    xs: 'text-[11px] px-2 py-0.5',
    sm: 'text-xs px-2.5 py-0.5',
    md: 'text-sm px-3 py-1',
};

const colorToken = (variant, color) => {
    const map = {
        primary: {
            solid: 'bg-gradient-to-b from-blue-500 to-blue-600 text-white shadow-sm shadow-blue-500/30',
            soft: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200/80',
            outline: 'text-blue-700 ring-1 ring-inset ring-blue-400 hover:bg-blue-50/50',
            ghost: 'text-blue-700 hover:bg-blue-50/80',
        },
        neutral: {
            solid: 'bg-gradient-to-b from-gray-600 to-gray-800 text-white shadow-sm shadow-gray-700/25',
            soft: 'bg-gray-100 text-gray-800 ring-1 ring-inset ring-gray-200/80',
            outline: 'text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50/80',
            ghost: 'text-gray-700 hover:bg-gray-100/80',
        },
        success: {
            solid: 'bg-gradient-to-b from-green-500 to-green-600 text-white shadow-sm shadow-green-500/30',
            soft: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-200/80',
            outline: 'text-green-700 ring-1 ring-inset ring-green-400 hover:bg-green-50/50',
            ghost: 'text-green-700 hover:bg-green-50/80',
        },
        warning: {
            solid: 'bg-gradient-to-b from-amber-400 to-amber-500 text-white shadow-sm shadow-amber-400/30',
            soft: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/80',
            outline: 'text-amber-700 ring-1 ring-inset ring-amber-400 hover:bg-amber-50/50',
            ghost: 'text-amber-700 hover:bg-amber-50/80',
        },
        danger: {
            solid: 'bg-gradient-to-b from-red-500 to-red-600 text-white shadow-sm shadow-red-500/30',
            soft: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200/80',
            outline: 'text-red-700 ring-1 ring-inset ring-red-400 hover:bg-red-50/50',
            ghost: 'text-red-700 hover:bg-red-50/80',
        },
    };
    return map[color]?.[variant] || map.neutral[variant];
};

export default function Tag({
    as: As = 'span',
    children,
    variant = 'soft',
    color = 'neutral',
    size = 'sm',
    leadingIcon,
    trailingIcon,
    removable = false,
    onRemove,
    onClick,
    disabled = false,
    className = '',
    style,
    ariaLabel,
}) {
    const classes = cx(
        'inline-flex items-center rounded-full font-medium select-none transition-colors duration-150',
        sizeMap[size],
        colorToken(variant, color),
        disabled && 'opacity-50 pointer-events-none',
        className
    );

    const handleRemove = (e) => {
        e.stopPropagation();
        onRemove?.();
    };

    return (
        <As
            className={classes}
            style={style}
            onClick={onClick}
            aria-label={ariaLabel}
            role={!children ? 'img' : undefined}
        >
            {leadingIcon && <span className="mr-1 inline-flex">{leadingIcon}</span>}
            {children && <span className="truncate">{children}</span>}
            {removable ? (
                <button
                    type="button"
                    onClick={handleRemove}
                    aria-label="Remove tag"
                    className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                >
                    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" aria-hidden="true">
                        <path
                            d="M6 6l8 8M14 6l-8 8"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                        />
                    </svg>
                </button>
            ) : (
                trailingIcon && <span className="ml-1 inline-flex">{trailingIcon}</span>
            )}
        </As>
    );
}